import { useMemo } from 'react';
import { Checkbox, Group, NumberInput, Paper, Stack, TextInput } from '@mantine/core';
import { useIntl } from 'react-intl';

import { freightFormMessages } from './freight-form-messages';
import { Dropdown } from '../../../common/Dropdown';

import type { InsuranceCommodityCategory, InsuranceItemCondition } from '../../../../types/freight-requests';
import type { FreightBrowserInsurance } from '../../freight-browser-types';

type InsuranceSectionProps = {
  value: FreightBrowserInsurance;
  onChange: (next: FreightBrowserInsurance) => void;
};

const CONDITION_VALUES: InsuranceItemCondition[] = ['New', 'Used'];

const CATEGORY_VALUES: InsuranceCommodityCategory[] = [
  'GeneralMerchandise',
  'Equipment',
  'FoodBeverages',
  'ChemicalsPaint',
  'Electronics',
  'Construction',
  'Furniture',
  'Stonework',
  'FragileItems',
  'Perishables',
  'Jewelry',
  'MotorizedTransport',
  'ArtAntiques',
  'Explosives',
  'LifeForms',
  'ControlledItems',
  'PharmaNonRefrigerated',
  'PharmaRefrigerated',
];

const CONDITION_MESSAGE_MAP: Record<InsuranceItemCondition, keyof typeof freightFormMessages> = {
  New: 'conditionNew',
  Used: 'conditionUsed',
};

const CATEGORY_MESSAGE_MAP: Record<InsuranceCommodityCategory, keyof typeof freightFormMessages> = {
  GeneralMerchandise: 'categoryGeneralMerchandise',
  Equipment: 'categoryEquipment',
  FoodBeverages: 'categoryFoodBeverages',
  ChemicalsPaint: 'categoryChemicalsPaint',
  Electronics: 'categoryElectronics',
  Construction: 'categoryConstruction',
  Furniture: 'categoryFurniture',
  Stonework: 'categoryStonework',
  FragileItems: 'categoryFragileItems',
  Perishables: 'categoryPerishables',
  Jewelry: 'categoryJewelry',
  MotorizedTransport: 'categoryMotorizedTransport',
  ArtAntiques: 'categoryArtAntiques',
  Explosives: 'categoryExplosives',
  LifeForms: 'categoryLifeForms',
  ControlledItems: 'categoryControlledItems',
  PharmaNonRefrigerated: 'categoryPharmaNonRefrigerated',
  PharmaRefrigerated: 'categoryPharmaRefrigerated',
};

export const InsuranceSection = ({ value, onChange }: InsuranceSectionProps) => {
  const intl = useIntl();

  const conditionOptions = useMemo(
    () => CONDITION_VALUES.map((v) => ({ value: v, label: intl.formatMessage(freightFormMessages[CONDITION_MESSAGE_MAP[v]]) })),
    [intl],
  );

  const categoryOptions = useMemo(
    () => CATEGORY_VALUES.map((v) => ({ value: v, label: intl.formatMessage(freightFormMessages[CATEGORY_MESSAGE_MAP[v]]) })),
    [intl],
  );

  return (
    <Paper p='md' withBorder>
      <Stack gap='md'>
        <Checkbox
          checked={value.isInsured}
          data-testid='insurance-checkbox'
          label={intl.formatMessage(freightFormMessages.insuranceLabel)}
          onChange={(e) => onChange({ ...value, isInsured: e.currentTarget.checked })}
          styles={{ label: { fontWeight: 600, fontSize: 'var(--mantine-font-size-md)' } }}
        />
        {value.isInsured && (
          <Stack gap='sm' pl='calc(var(--checkbox-size, 20px) + var(--mantine-spacing-sm))'>
            <Group grow>
              <Dropdown
                data-testid='insurance-condition'
                data={conditionOptions}
                label={intl.formatMessage(freightFormMessages.conditionOfGoodsLabel)}
                onChange={(val) => onChange({ ...value, conditionOfGoods: val ?? '' })}
                placeholder={intl.formatMessage(freightFormMessages.selectOnePlaceholder)}
                value={value.conditionOfGoods || null}
              />
              <Dropdown
                data-testid='insurance-category'
                data={categoryOptions}
                label={intl.formatMessage(freightFormMessages.categoryLabel)}
                onChange={(val) => onChange({ ...value, category: val ?? '' })}
                placeholder={intl.formatMessage(freightFormMessages.selectOnePlaceholder)}
                value={value.category || null}
              />
            </Group>
            <Group grow>
              <NumberInput
                allowNegative={false}
                data-testid='insurance-value'
                label={intl.formatMessage(freightFormMessages.insuredValueLabel)}
                min={0}
                onChange={(val) => onChange({ ...value, insuredValue: typeof val === 'string' ? parseFloat(val) || 0 : val })}
                prefix='$ '
                value={value.insuredValue}
              />
              <TextInput
                data-testid='insurance-marks'
                label={intl.formatMessage(freightFormMessages.marksAndNumbersLabel)}
                onChange={(e) => onChange({ ...value, marksAndNumbers: e.currentTarget.value })}
                placeholder='Enter'
                value={value.marksAndNumbers}
              />
            </Group>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
};
